import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import ApiService from "@/core/services/ApiService";
import { Exchange } from "@/core/services/api/interfaces/manager/Exchange";

type Index = string | number;

export interface ExchangeParams {
  sell: number | string | null;
  buy: number | string | null;
  timerValue?: number | null;
  force?: boolean;
}

interface StateQ {
  sell: Index;
  buy: Index;
  storage?: null | ExchangeStorage;
}

interface ExchangeStorage {
  timer: number;
  interval: null | ReturnType<typeof setInterval>;
  timerValue: number | null;
  loading: boolean;
  exchange: null | Exchange;
}

@Module
export default class ExchangeRatesModule extends VuexModule {
  storage: { [index: Index]: { [index: Index]: ExchangeStorage } } = {};

  get getExactStorage() {
    return ({ sell, buy }): null | ExchangeStorage => {
      if (!sell || !(sell in this.storage)) {
        return null;
      }
      if (!buy || !(buy in this.storage[sell])) {
        return null;
      }

      return this.storage[sell][buy];
    };
  }
  get hasExactStorage() {
    return ({ sell, buy }): boolean => {
      return null !== this.getExactStorage({ sell, buy });
    };
  }

  get getRatesTimer() {
    return ({ sell, buy }): null | number => {
      if (!this.hasExactStorage({ sell, buy })) {
        return null;
      }

      return this.storage[sell][buy].timer;
    };
  }

  get getRatesInterval() {
    return ({ sell, buy }): null | ReturnType<typeof setInterval> => {
      if (!this.hasExactStorage({ sell, buy })) {
        return null;
      }

      return this.storage[sell][buy].interval;
    };
  }

  get getRatesExchange() {
    return ({ sell, buy }): null | Exchange => {
      if (!this.hasExactStorage({ sell, buy })) {
        return null;
      }

      return this.storage[sell][buy].exchange;
    };
  }

  get isRatesLoading() {
    return ({ sell, buy }): null | boolean => {
      if (!this.hasExactStorage({ sell, buy })) {
        return null;
      }

      return this.storage[sell][buy].loading;
    };
  }

  @Action({ rawError: true })
  [Actions.COMMON_RATES_REFRESH](params: ExchangeParams) {
    if (!params.sell || !params.buy) {
      console.log("sell or buy is not defined", params.sell, params.buy);

      return;
    }

    if (this.getRatesTimer(params)) {
      return this.context.commit(Mutations.COMMON_RATES_TIMER_TAP, params);
    }
    if (this.isRatesLoading(params)) {
      return;
    }
    this.context.commit(Mutations.COMMON_RATES_LOADING, {
      sell: params.sell,
      buy: params.buy,
      loading: true,
    });
    const isTransfer =
      typeof params.sell === "number" && !Number.isNaN(params.sell);

    const isAuthed = this.context.getters.isUserAuthenticated;
    console.log("isTransfer", isTransfer);
    console.log("isAuthed", isAuthed);

    const callee = isTransfer
      ? ApiService.manager.exchange.rates
      : isAuthed
      ? ApiService.manager.exchange.ratesByCurrency
      : ApiService.common.exchange.rates;

    return callee(params.sell, params.buy)
      .then(ApiService.unwrapItemResponse)
      .then((exchange) => {
        this.context.commit(Mutations.COMMON_RATES_CHANGE_STATE_VALUE, {
          sell: params.sell,
          buy: params.buy,
          prop: "exchange",
          value: exchange,
        });

        this.context.commit(Mutations.COMMON_RATES_TIMER_REFRESH, params);
      })
      .finally(() => {
        this.context.commit(Mutations.COMMON_RATES_LOADING, {
          sell: params.sell,
          buy: params.buy,
          loading: false,
        });
      });
  }

  @Action
  [Actions.COMMON_RATES_STOP_INTERVAL]({ sell, buy }) {
    const storage = this.getExactStorage({ sell, buy });
    if (!storage) {
      return;
    }
    const interval = this.getRatesInterval({ sell, buy });
    if (!interval) {
      return;
    }
    console.log("---|| rates ||---", sell, buy);
    clearInterval(interval);

    this.context.commit(Mutations.COMMON_RATES_CHANGE_STATE_VALUE, {
      sell,
      buy,
      prop: "interval",
      value: null,
    });

    this.context.commit(Mutations.COMMON_RATES_CHANGE_STATE_VALUE, {
      sell,
      buy,
      prop: "timer",
      value: 0,
    });
  }

  @Action
  [Actions.COMMON_RATES_STOP_INTERVAL_BY_ID](
    interval: ReturnType<typeof setInterval>
  ) {
    if (!interval) {
      return;
    }
    for (const sell in this.storage) {
      for (const buy in this.storage[sell]) {
        const storage = this.storage[sell][buy];
        if (storage.interval === interval) {
          return this.context.dispatch(Actions.COMMON_RATES_STOP_INTERVAL, {
            sell,
            buy,
          });
        }
      }
    }

    return;
  }

  @Action({ rawError: true })
  async [Actions.COMMON_RATES_START](params: ExchangeParams) {
    if (!this.hasExactStorage(params)) {
      this.context.commit(Mutations.COMMON_RATES_SETUP_STATE, params);
    }

    await this.context.dispatch(Actions.COMMON_RATES_STOP_INTERVAL, params);

    this.context.commit(Mutations.COMMON_RATES_CHANGE_STATE_VALUE, {
      sell: params.sell,
      buy: params.buy,
      prop: "timerValue",
      value: params.timerValue,
    });
    console.log("---> rates <---", params.sell, params.buy);

    return this.context
      .dispatch(Actions.COMMON_RATES_REFRESH, params)
      .then(() =>
        this.context.dispatch(Actions.COMMON_RATES_STOP_INTERVAL, params)
      )
      .then(() => {
        if (!("timerValue" in params) || !params.timerValue) {
          return null;
        }

        const interval = setInterval(() => {
          return this.context.dispatch(Actions.COMMON_RATES_REFRESH, params);
        }, 1000);

        this.context.commit(Mutations.COMMON_RATES_CHANGE_STATE_VALUE, {
          sell: params.sell,
          buy: params.buy,
          prop: "interval",
          value: interval,
        });

        return interval;
      });
  }

  @Mutation
  [Mutations.COMMON_RATES_SETUP_STATE](state: StateQ) {
    if (!(state.sell in this.storage)) {
      this.storage[state.sell] = {};
    }

    if (!(state.buy in this.storage[state.sell])) {
      this.storage[state.sell][state.buy] = {
        timer: 0,
        interval: null,
        timerValue: null,
        loading: false,
        exchange: null,
      } as ExchangeStorage;
    }
    if ("storage" in state && state.storage) {
      this.storage[state.sell][state.buy] = Object.assign(
        this.storage[state.sell][state.buy],
        state.storage
      );
    }
  }

  @Mutation
  [Mutations.COMMON_RATES_CHANGE_STATE_VALUE]({ sell, buy, prop, value }) {
    if (!(sell in this.storage)) {
      return;
    }
    if (!buy || !(buy in this.storage[sell])) {
      return;
    }

    this.storage[sell][buy][prop] = value;
  }

  @Mutation
  [Mutations.COMMON_RATES_TIMER_TAP]({ sell, buy }) {
    if (!(sell in this.storage)) {
      return;
    }
    if (!buy || !(buy in this.storage[sell])) {
      return;
    }

    --this.storage[sell][buy].timer;
  }

  @Mutation
  [Mutations.COMMON_RATES_TIMER_REFRESH]({ sell, buy }) {
    if (!(sell in this.storage)) {
      return;
    }
    if (!buy || !(buy in this.storage[sell])) {
      return;
    }
    const timerValue = this.storage[sell][buy].timerValue;
    if (!timerValue) {
      return;
    }

    this.storage[sell][buy].timer = timerValue;
  }

  @Mutation
  [Mutations.COMMON_RATES_LOADING]({ sell, buy, loading }) {
    if (!(sell in this.storage)) {
      return;
    }
    if (!buy || !(buy in this.storage[sell])) {
      return;
    }
    this.storage[sell][buy].loading = loading;
  }
}
